import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import LandingHeader from '../components/organisms/LandingHeader';
import JobCard from '../components/molecules/JobCard';
import Icon from '../components/atoms/Icon';
import InterviewContinuationBanner from '../components/InterviewContinuationBanner';
import SessionManager from '../utils/sessionManager';
import { getJobs } from '../services/api';
import { API_BASE_URL } from '../config';

const companyLogoFallback = (company = '') =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent(company)}&background=2563eb&color=fff`;

const companyLogos = {
  Google: 'https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg',
  Apple: 'https://upload.wikimedia.org/wikipedia/commons/f/fa/Apple_logo_black.svg',
  Microsoft: 'https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg',
  Amazon: 'https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg',
  Infosys: 'https://upload.wikimedia.org/wikipedia/commons/9/95/Infosys_logo.svg',
  TCS: 'https://upload.wikimedia.org/wikipedia/commons/b/b1/Tata_Consultancy_Services_Logo.svg',
  Accenture: 'https://upload.wikimedia.org/wikipedia/commons/c/cd/Accenture.svg',
  Deloitte: 'https://upload.wikimedia.org/wikipedia/commons/5/56/Deloitte.svg',
  Meta: 'https://upload.wikimedia.org/wikipedia/commons/8/8d/Meta_Platforms_Inc._logo.svg',
  Spotify: 'https://upload.wikimedia.org/wikipedia/commons/2/26/Spotify_logo_with_text.svg',
  Capgemini: 'https://upload.wikimedia.org/wikipedia/commons/9/9d/Capgemini_201x_logo.svg',
  Airbnb: 'https://upload.wikimedia.org/wikipedia/commons/6/69/Airbnb_Logo_Bélo.svg',
};

const JobsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const sidebarRef = useRef(null);
  const [isSticky, setIsSticky] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);
  const [appliedJobMap, setAppliedJobMap] = useState({}); // job_id -> application status

  const defaultFilters = {
    search: '',
    location: '',
    experienceLevel: [],
    jobType: [],
    selectedRoles: [],
  };

  const [filters, setFilters] = useState(defaultFilters);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const jobsPerPage = 12;
  
  // Dropdown states
  const [showCategoriesDropdown, setShowCategoriesDropdown] = useState(false);
  const [showExperienceDropdown, setShowExperienceDropdown] = useState(false);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);

  const jobRoleFilters = {
    'IT Roles': [
      'Software Engineer',
      'Frontend Developer',
      'Backend Developer',
      'Full Stack Developer',
      'Data Scientist',
      'Cloud Architect',
      'Cybersecurity Analyst',
      'DevOps Engineer',
      'Mobile Developer',
      'QA Engineer',
    ],
    'Non-IT Roles': [
      'Product Manager',
      'Digital Marketer',
      'Financial Analyst',
      'HR Business Partner',
      'Operations Manager',
      'UX Researcher',
      'Business Analyst',
      'Sales Executive',
      'Content Writer',
      'Graphic Designer',
    ],
  };

  const experienceLevels = [
    { label: 'Entry Level (0-2 years)', value: 'entry' },
    { label: 'Mid Level (2-5 years)', value: 'mid' },
    { label: 'Senior (5-10 years)', value: 'senior' },
    { label: 'Lead (10+ years)', value: 'lead' },
  ];

  const popularLocations = [
    'New York, NY',
    'San Francisco, CA',
    'Austin, TX',
    'Seattle, WA',
    'Boston, MA',
    'Chicago, IL',
    'Los Angeles, CA',
    'Denver, CO',
    'Remote',
  ];

  // Check session on mount and fetch applied jobs
  useEffect(() => {
    const session = SessionManager.getCurrentSession();
    setCurrentSession(session);

    // If candidate, fetch their applications to mark applied jobs
    const candidateSession = SessionManager.getSession('candidate');
    if (candidateSession?.user_id) {
      fetch(`${API_BASE_URL}/candidates/${candidateSession.user_id}/applications`)
        .then(res => res.ok ? res.json() : null)
        .then(data => {
          if (data?.applications) {
            const map = {};
            data.applications.forEach(app => {
              map[app.job_id] = app.status;
            });
            setAppliedJobMap(map);
          }
        })
        .catch(() => {});
    }
  }, []);

  // Scroll handler for sticky sidebar
  useEffect(() => {
    const handleScroll = () => {
      const heroHeight = 280; // Height of hero banner
      setIsSticky(window.scrollY > heroHeight);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const mapFiltersToQuery = (sourceFilters) => {
    const workType = sourceFilters.jobType.find((t) => t === 'remote') ? 'remote' : undefined;
    return {
      search: sourceFilters.search || undefined,
      location: sourceFilters.location || undefined,
      experience_level: sourceFilters.experienceLevel[0] || undefined,
      work_type: workType,
    };
  };

  const fetchJobs = useCallback(
    async (sourceFilters) => {
      const filtersToUse = sourceFilters || filters;
      setLoading(true);
      setError('');
      try {
        const params = mapFiltersToQuery(filtersToUse);
        const data = await getJobs(params);
        setJobs(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err.message);
        setJobs([]);
      } finally {
        setLoading(false);
      }
    },
    [filters],
  );

  useEffect(() => {
    fetchJobs(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const title = params.get('title') || '';
    const place = params.get('location') || '';
    if (title || place) {
      const updatedFilters = {
        ...filters,
        search: title,
        location: place,
      };
      setFilters(updatedFilters);
      fetchJobs(updatedFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const formatRelativeTime = (value) => {
    if (!value) return 'Just now';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Just now';
    const diff = Date.now() - date.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes} min${minutes > 1 ? 's' : ''} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hr${hours > 1 ? 's' : ''} ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  };

  const jobsWithPresentation = useMemo(
    () =>
      jobs
        .filter((job) => {
          const status = job.status;
          return status !== 'Closed' && (status === 'Active' || !status);
        })
        .map((job) => ({
          ...job,
          role: job.title,
          workType: job.work_type,
          experienceLevel: job.experience_level,
          postedDate: formatRelativeTime(job.posted_date),
          logo: companyLogos[job.company] || companyLogoFallback(job.company),
        })),
    [jobs],
  );

  const filteredJobs = jobsWithPresentation.filter((job) => {
    const matchesSearch =
      !filters.search ||
      job.title.toLowerCase().includes(filters.search.toLowerCase()) ||
      job.description.toLowerCase().includes(filters.search.toLowerCase()) ||
      job.role.toLowerCase().includes(filters.search.toLowerCase());

    const matchesLocation =
      !filters.location ||
      job.location.toLowerCase().includes(filters.location.toLowerCase());

    const matchesExperience =
      filters.experienceLevel.length === 0 ||
      filters.experienceLevel.includes(job.experienceLevel);

    const matchesJobType =
      filters.jobType.length === 0 ||
      filters.jobType.some(
        (type) =>
          job.type.toLowerCase() === type || job.workType.toLowerCase() === type,
      );

    const matchesRole =
      filters.selectedRoles.length === 0 ||
      filters.selectedRoles.includes(job.role);

    return (
      matchesSearch &&
      matchesLocation &&
      matchesExperience &&
      matchesJobType &&
      matchesRole
    );
  });

  const resetFilters = () => {
    setFilters(defaultFilters);
    setCurrentPage(1);
    fetchJobs(defaultFilters);
    navigate('/jobs', { replace: true });
  };

  // Calculate pagination
  const totalJobs = filteredJobs.length;
  const totalPages = Math.ceil(totalJobs / jobsPerPage);
  const startIndex = (currentPage - 1) * jobsPerPage;
  const endIndex = startIndex + jobsPerPage;
  const currentJobs = filteredJobs.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Scroll to top only when page number changes (not on initial load)
  useEffect(() => {
    if (currentPage > 1) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentPage]);

  const activeFiltersCount = 
    filters.experienceLevel.length + 
    filters.jobType.length + 
    filters.selectedRoles.length +
    (filters.location ? 1 : 0);

  // Check if user can post jobs
  const canPostJob = !currentSession || currentSession.role === 'hr';
  const isCandidate = currentSession?.role === 'candidate';

  return (
    <div className="relative flex min-h-screen w-full flex-col bg-gray-50">
      <InterviewContinuationBanner />
      
      <LandingHeader />
      
      {/* Hero Banner - Dark Blue like Courses */}
      <section className="bg-[#0a1929] text-white py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Aptirix Jobs
            </h1>
            <p className="text-lg md:text-xl text-gray-300 mb-6">
              India's First AI-Powered Job Platform
            </p>
            <p className="text-base text-gray-400 leading-relaxed">
              Discover your next career opportunity with AI-powered job matching. 
              Connect with top companies, get personalized recommendations, and accelerate your career growth 
              with intelligent interview preparation.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="relative flex">
        {/* Left Sidebar - Filters - Sticky on Desktop */}
        <aside className="hidden lg:block w-64 bg-white border-r border-gray-200 flex-shrink-0">
          <div className="sticky top-20 p-6 max-h-[calc(100vh-5rem)] overflow-y-auto">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Filters</h3>
              
              <div className="space-y-6">
            {/* New Job Button - Conditional */}
            {canPostJob && (
              <button
                onClick={() => {
                  if (currentSession) {
                    navigate('/hr/post-job');
                  } else {
                    navigate('/recruiter', { state: { scrollToForm: true } });
                  }
                }}
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg flex items-center justify-center space-x-2 transition-colors shadow-md hover:shadow-lg"
              >
                <Icon name="add" size={20} />
                <span>New Job</span>
              </button>
            )}

            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search Jobs
              </label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search..."
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <Icon name="search" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              </div>
            </div>

            {/* Categories Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Job Categories
              </label>
              <button
                onClick={() => {
                  setShowCategoriesDropdown(!showCategoriesDropdown);
                  setShowExperienceDropdown(false);
                  setShowLocationDropdown(false);
                }}
                className="w-full flex items-center justify-between px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <span>{filters.selectedRoles.length > 0 ? `${filters.selectedRoles.length} selected` : 'Select categories'}</span>
                <Icon name={showCategoriesDropdown ? "expand_less" : "expand_more"} size={18} />
              </button>
              
              {showCategoriesDropdown && (
                <div className="mt-2 p-3 bg-gray-50 rounded-lg max-h-80 overflow-y-auto border border-gray-200">
                  {Object.entries(jobRoleFilters).map(([group, roles]) => (
                    <div key={group} className="mb-4 last:mb-0">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                        {group}
                      </p>
                      <div className="space-y-1">
                        {roles.map((role) => (
                          <label key={role} className="flex items-center gap-2 cursor-pointer hover:bg-white p-2 rounded transition-colors">
                            <input
                              type="checkbox"
                              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              checked={filters.selectedRoles.includes(role)}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setFilters({
                                  ...filters,
                                  selectedRoles: checked
                                    ? [...filters.selectedRoles, role]
                                    : filters.selectedRoles.filter((r) => r !== role),
                                });
                              }}
                            />
                            <span className="text-sm text-gray-700">{role}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Experience Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Experience Level
              </label>
              <button
                onClick={() => {
                  setShowExperienceDropdown(!showExperienceDropdown);
                  setShowCategoriesDropdown(false);
                  setShowLocationDropdown(false);
                }}
                className="w-full flex items-center justify-between px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <span>{filters.experienceLevel.length > 0 ? `${filters.experienceLevel.length} selected` : 'Select experience'}</span>
                <Icon name={showExperienceDropdown ? "expand_less" : "expand_more"} size={18} />
              </button>
              
              {showExperienceDropdown && (
                <div className="mt-2 p-3 bg-gray-50 rounded-lg space-y-1 border border-gray-200">
                  {experienceLevels.map(({ label, value }) => (
                    <label key={value} className="flex items-center gap-2 cursor-pointer hover:bg-white p-2 rounded transition-colors">
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        checked={filters.experienceLevel.includes(value)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setFilters({
                            ...filters,
                            experienceLevel: checked
                              ? [...filters.experienceLevel, value]
                              : filters.experienceLevel.filter((l) => l !== value),
                          });
                        }}
                      />
                      <span className="text-sm text-gray-700">{label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Location Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location
              </label>
              <button
                onClick={() => {
                  setShowLocationDropdown(!showLocationDropdown);
                  setShowCategoriesDropdown(false);
                  setShowExperienceDropdown(false);
                }}
                className="w-full flex items-center justify-between px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <span>{filters.location || 'Select location'}</span>
                <Icon name={showLocationDropdown ? "expand_less" : "expand_more"} size={18} />
              </button>
              
              {showLocationDropdown && (
                <div className="mt-2 p-3 bg-gray-50 rounded-lg space-y-1 border border-gray-200">
                  {popularLocations.map((loc) => (
                    <button
                      key={loc}
                      onClick={() => {
                        setFilters({ ...filters, location: filters.location === loc ? '' : loc });
                      }}
                      className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${
                        filters.location === loc
                          ? 'bg-blue-50 text-blue-700 font-medium'
                          : 'hover:bg-white text-gray-700'
                      }`}
                    >
                      {loc}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Clear Filters */}
            {activeFiltersCount > 0 && (
              <button
                onClick={resetFilters}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors border border-blue-200"
              >
                <Icon name="close" size={18} />
                <span>Clear all filters ({activeFiltersCount})</span>
              </button>
            )}
              </div>
            </div>
          </aside>

        {/* Main Content Area */}
        <div className="flex-1">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
            {/* Main Job Listings */}
            <main className="w-full">
            {/* Top Bar - Results Count and Sort */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="text-gray-700">
                  <span className="font-semibold">{totalJobs}</span> jobs found
                  {totalPages > 1 && <span className="text-gray-500"> • Page {currentPage} of {totalPages}</span>}
                </div>
              </div>
            </div>

            {/* Job Grid */}
            {loading ? (
              <div className="flex justify-center py-20">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading jobs...</p>
                </div>
              </div>
            ) : error ? (
              <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-700">
                {error}
              </div>
            ) : filteredJobs.length === 0 ? (
              <div className="rounded-xl border border-gray-200 bg-white p-12 text-center">
                <Icon name="work_off" size={64} className="text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  No jobs found
                </h3>
                <p className="text-gray-600 mb-6">
                  Try adjusting your filters or search criteria
                </p>
                <button
                  onClick={resetFilters}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Reset Filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {currentJobs.map((job) => (
                  <JobCard key={job.id} job={job} applicationStatus={appliedJobMap[job.id]} onViewDetails={() => navigate(`/jobs/${job.id}`)} />
                ))}
              </div>
            )}
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-8 flex items-center justify-center gap-2">
                <button 
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                
                <div className="flex gap-2">
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                          currentPage === pageNum
                            ? 'bg-blue-600 text-white'
                            : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button 
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            )}
            </main>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobsPage;
